import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';
import { isChinesePath, stripLanguagePrefix, isZhComplete } from '../lib/languagePaths';

const CANONICAL_BASE = process.env.REACT_APP_CANONICAL_BASE || 'https://cyberate.investments';
const INDEXABLE = process.env.REACT_APP_INDEXABLE === '1';

const DEFAULT_DESCRIPTION =
  'Cyberate Investments Pty Ltd is an Adelaide-based development and investment company focused on property, built-environment businesses and enabling innovation.';

// 绝对 URL：英文首页 '/'，英文其它 '/about'；中文首页 '/zh/'，中文其它 '/zh/about'。
const buildUrl = (path, lang) => {
  if (lang === 'zh') return path === '/' ? `${CANONICAL_BASE}/zh/` : `${CANONICAL_BASE}/zh${path}`;
  return path === '/' ? `${CANONICAL_BASE}/` : `${CANONICAL_BASE}${path}`;
};

const SEO = ({ title, description, path, noindex, schema }) => {
  // 当前语言由 URL 决定（/zh 前缀）。path 是 basename 之下的相对路径（不含 /zh）。
  const isZh = typeof window !== 'undefined' && isChinesePath(window.location.pathname);
  const cleanPath = path ? stripLanguagePrefix(path) : undefined;

  // 中文页面是否已完整翻译：未翻译的 zh 页在 zh 侧 noindex，且不输出 zh-CN hreflang，
  // 避免「英文内容顶着 zh-CN URL」被收录（X06 / 06 §2.5）。
  const zhComplete = cleanPath ? isZhComplete(cleanPath) : false;

  // canonical 自引用当前语言版本；en/zh 两个 alternate 互为对等页，x-default 指向英文。
  const canonical = cleanPath ? buildUrl(cleanPath, isZh ? 'zh' : 'en') : undefined;
  const enUrl = cleanPath ? buildUrl(cleanPath, 'en') : undefined;
  const zhUrl = cleanPath && zhComplete ? buildUrl(cleanPath, 'zh') : undefined;

  const robots = noindex || !INDEXABLE || (isZh && !zhComplete) ? 'noindex, follow' : 'index, follow';

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="robots" content={robots} />
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      {canonical && <link rel="canonical" href={canonical} />}
      {enUrl && <link rel="alternate" hreflang="en-AU" href={enUrl} />}
      {zhUrl && <link rel="alternate" hreflang="zh-CN" href={zhUrl} />}
      {enUrl && <link rel="alternate" hreflang="x-default" href={enUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:type" content="website" />
      {canonical && <meta property="og:url" content={canonical} />}
      {schema &&
        schema.map((item, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  noindex: PropTypes.bool,
  schema: PropTypes.array,
};

export default SEO;
